import { useCallback } from 'react';

import { usePaymentContext } from 'features/payment';
import { PaymentRequestError, RequestPaymentFn, paymentErrorType } from 'features/payment/types';
import { getFraudPreventionValues } from 'features/payment/utils';

import { getNonce } from 'remote/api/first-data';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { getConfigValue } from 'utils/environment';
import { logger } from 'utils/logger';

export const useFirstDataCardPayment: RequestPaymentFn = ({ firsDataCard }) => {
  const { paymentValues } = firsDataCard ?? {};
  const fdUrl = getConfigValue({ key: 'firstData', defaultValue: {} })?.baseUrl;
  const onlySendPostalCode = useFlag(LaunchDarklyFlag.SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT);
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;

  const { getEncryptionDetails, transformPaymentValues } = usePaymentContext();

  const requestPayment = useCallback(async () => {
    if (!paymentValues) {
      logger.error({
        isPaymentLog: true,
        message: '[useFirstDataCardPayment][requestPayment] Missing payment values.',
        error: new Error('Payment values are undefined or null'),
      });
      throw new PaymentRequestError(paymentErrorType.firstDataCreditCardGeneric);
    }

    try {
      const reshapedPaymentValues = transformPaymentValues({
        paymentValues,
        paymentFieldVariations,
      });
      const fraudPreventionValues = getFraudPreventionValues(paymentValues);

      const response = await getEncryptionDetails();
      const fdAccessToken = response.fdAccessToken;
      const nonceResponse = await getNonce(
        reshapedPaymentValues,
        response.fdPublicKey,
        response.fdApiKey,
        fdAccessToken,
        response.fdCustomerId ?? null,
        fdUrl,
        onlySendPostalCode,
        response.algorithm ?? undefined
      );

      const nonce = await nonceResponse.json();
      const paymentNonce = nonce.token.tokenId;

      return {
        fdAccessToken,
        fdNonce: paymentNonce,
        creditType: paymentValues.cardType,
        fraudPreventionValues,
        saveCard: paymentValues.saveCard,
        state: paymentValues,
      };
    } catch (error) {
      logger.error({
        isPaymentLog: true,
        message:
          '[useFirstDataCardPayment][requestPayment] Error occurred while processing First Data card payment.',
        error,
      });
      throw error instanceof PaymentRequestError
        ? error
        : new PaymentRequestError(paymentErrorType.firstDataCreditCardGeneric);
    }
  }, [
    fdUrl,
    getEncryptionDetails,
    onlySendPostalCode,
    paymentFieldVariations,
    paymentValues,
    transformPaymentValues,
  ]);

  return { requestPayment };
};
